import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ArrowRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip accents so "chichen" matches "Chichén"
const normalize = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Highlight the matched substring in a result
const Highlight = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const normText = normalize(text);
  const normQ = normalize(query);
  const idx = normText.indexOf(normQ);
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-[#006D77] font-bold">{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
};

const PredictiveSearch = () => {
  const [query, setQuery] = useState("");
  const [tours, setTours] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "en";

  // Fetch all tours once for instant client-side filtering
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/tours?lang=${lang}`)
      .then((r) => {
        if (!cancelled) setTours(r.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [lang]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return [];
    return tours
      .filter((tr) => {
        const title = normalize(tr.title);
        const desc = normalize(tr.short_description);
        return title.includes(q) || desc.includes(q);
      })
      .slice(0, 6);
  }, [query, tours]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIdx(0);
  }, [results.length]);

  const goToTour = (tour) => {
    setOpen(false);
    setQuery("");
    navigate(`/tours/${tour.slug}`);
  };

  const submitSearch = () => {
    const q = query.trim();
    if (!q) return;
    setOpen(false);
    if (results.length === 1) {
      goToTour(results[0]);
    } else {
      navigate(`/tours?q=${encodeURIComponent(q)}`);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(results.length - 1, i + 1));
      setOpen(true);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && results[activeIdx]) {
        goToTour(results[activeIdx]);
      } else {
        submitSearch();
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full max-w-2xl" data-testid="predictive-search">
      <div
        className={`relative flex items-center bg-white/85 backdrop-blur-xl rounded-2xl shadow-2xl border-2 transition-all ${
          open ? "border-[#006D77]" : "border-white/40"
        }`}
      >
        <Search className="w-5 h-5 sm:w-6 sm:h-6 text-[#006D77] ml-4 sm:ml-5 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={t("search.placeholder")}
          aria-label={t("search.placeholder")}
          data-testid="hero-search-input"
          className="flex-1 bg-transparent outline-none px-3 sm:px-4 py-3 sm:py-4 text-sm sm:text-base text-[#1D3557] placeholder:text-[#64748B] min-w-0"
        />
        {query && (
          <button
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            data-testid="hero-search-clear"
            className="p-2 text-[#64748B] hover:text-[#006D77] flex-shrink-0"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={submitSearch}
          data-testid="hero-search-submit"
          className="m-1.5 sm:m-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-[#006D77] hover:bg-[#005a63] text-white rounded-xl font-semibold flex items-center gap-1.5 text-sm sm:text-base transition-colors flex-shrink-0"
          aria-label={t("search.go")}
        >
          <span className="hidden sm:inline">{t("search.go")}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <AnimatePresence>
        {open && query.trim().length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] overflow-hidden z-50"
            data-testid="hero-search-dropdown"
          >
            {results.length > 0 ? (
              <ul className="max-h-[60vh] overflow-y-auto py-1">
                {results.map((tour, idx) => (
                  <li key={tour.id}>
                    <button
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => goToTour(tour)}
                      data-testid={`hero-search-result-${tour.slug}`}
                      className={`w-full flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-left transition-colors ${
                        idx === activeIdx ? "bg-[#006D77]/8" : "hover:bg-[#006D77]/5"
                      }`}
                    >
                      <img
                        src={`${tour.image_url}${tour.image_url?.includes("?") ? "&" : "?"}w=80&q=70&fm=webp&fit=crop&auto=format`}
                        alt=""
                        width="48"
                        height="48"
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#1D3557] text-sm sm:text-base truncate">
                          <Highlight text={tour.title} query={query} />
                        </p>
                        <p className="text-xs text-[#64748B] flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" />
                          {t(`tour_card.cat_${tour.category}`)}
                          <span className="mx-1">•</span>
                          <span className="text-[#006D77] font-semibold">
                            ${Math.round(tour.price)}
                          </span>
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#64748B] flex-shrink-0" />
                    </button>
                  </li>
                ))}
                <li className="border-t border-[#E2E8F0] mt-1">
                  <button
                    onClick={submitSearch}
                    data-testid="hero-search-view-all"
                    className="w-full px-4 py-2.5 text-sm font-semibold text-[#006D77] hover:bg-[#006D77]/5 text-center"
                  >
                    {t("search.view_all").replace("{q}", query)}
                  </button>
                </li>
              </ul>
            ) : (
              <div className="px-4 py-6 text-center" data-testid="hero-search-empty">
                <p className="text-sm text-[#64748B]">{t("search.no_results")}</p>
                <button
                  onClick={() => navigate("/tours")}
                  className="mt-2 text-sm font-semibold text-[#006D77] hover:underline"
                >
                  {t("search.browse_all")}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PredictiveSearch;
